/**
 * Spec 027: while the platform owner sets a brand's default environment to Testing, the brand's
 * production website sends visitors to the brand's testing site (for Lenvoy's store review on
 * staging before launch). Pure rules here; `applyBrandDefaultEnvironment` runs them at start.
 *
 * Kept out of the way of the owner: admin pages and the sign-in return are never redirected, and
 * a visitor who opened `/?env=production` stays on production in that browser.
 */
import {
  fetchEnvironmentSettings,
  readCachedEnvironmentSettings,
  type BrandEnvironmentSettings,
} from './environmentSettings';
import { readProductionChoice, storeProductionChoice } from './brandEnvChoice';
import { getCurrentRuntimeTarget, getGraphqlBaseUrl } from './runtime-target';

/** Paths that are never redirected: the admin (the owner switches the default back from there)
 * and the Cognito return (sign-in must finish where it began). */
const NEVER_REDIRECTED_PATHS = ['/admin', '/auth/'];

/** How long the start waits for production's answer when the last one said Testing. */
export const BRAND_ENV_CONFIRM_TIMEOUT_MS = 1500;

/**
 * How long a first visit (nothing cached) waits before rendering. Without it the production site
 * rendered, started its own requests and could ask for permissions before moving away. Past it
 * the page renders and still moves when the answer arrives.
 */
export const BRAND_ENV_FIRST_VISIT_TIMEOUT_MS = 1000;

/**
 * Query parameters of a return that must finish here, this load only (the next load moves on):
 * the cross-site sign-in code is one-time and was issued for production, and a bank check (3DS)
 * returns to the page that started the payment.
 */
const IN_FLIGHT_PARAMS = [
  'sso_code',
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
];

export function hasInFlightReturn(search: string): boolean {
  const params = new URLSearchParams(search);
  return IN_FLIGHT_PARAMS.some(name => params.has(name));
}

export type BrandEnvironmentRedirect =
  | { kind: 'stay' }
  | { kind: 'storeChoice'; cleanedUrl: string }
  | { kind: 'redirect'; url: string };

export function isNeverRedirectedPath(pathname: string): boolean {
  return NEVER_REDIRECTED_PATHS.some(prefix =>
    prefix.endsWith('/')
      ? pathname.startsWith(prefix)
      : pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** The brand's own production domain (with or without `www.`), nothing else. */
export function isBrandProductionHost(hostname: string, brandSite: string): boolean {
  const host = hostname.toLowerCase();
  return host === brandSite || host === `www.${brandSite}`;
}

export function decideBrandEnvironmentRedirect(input: {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
  brandSite: string;
  stagingWebHost?: string | null;
  /** `getCurrentRuntimeTarget()`: only the production build on the brand's domain redirects. */
  target: 'development' | 'staging' | 'production';
  settings: Pick<BrandEnvironmentSettings, 'defaultEnvironment'> | null;
  visitorChoseProduction: boolean;
}): BrandEnvironmentRedirect {
  const { stagingWebHost, target, settings } = input;
  if (!stagingWebHost || target !== 'production') return { kind: 'stay' };
  if (!isBrandProductionHost(input.hostname, input.brandSite)) return { kind: 'stay' };

  const params = new URLSearchParams(input.search);
  if (params.get('env') === 'production') {
    params.delete('env');
    const rest = params.toString();
    return {
      kind: 'storeChoice',
      cleanedUrl: `${input.pathname}${rest ? `?${rest}` : ''}${input.hash}`,
    };
  }
  if (input.visitorChoseProduction) return { kind: 'stay' };
  if (isNeverRedirectedPath(input.pathname)) return { kind: 'stay' };
  if (hasInFlightReturn(input.search)) return { kind: 'stay' };
  if (settings?.defaultEnvironment !== 'STAGING') return { kind: 'stay' };
  return {
    kind: 'redirect',
    url: `https://${stagingWebHost}${input.pathname}${input.search}${input.hash}`,
  };
}

/** The URL of production's GraphQL: this origin's own when the brand host proxies it. */
export function settingsGraphqlUrl(): string {
  return `${getGraphqlBaseUrl()}graphql`;
}

/**
 * Runs before React renders. Returns true when the page is being sent to the testing site (do
 * not render).
 * - Cached Testing: waits up to `BRAND_ENV_CONFIRM_TIMEOUT_MS` for the fresh answer, so an owner
 *   who switched back to Production is not bounced by a stale cache; no answer trusts the cache.
 * - Nothing cached (a first visit): waits up to `BRAND_ENV_FIRST_VISIT_TIMEOUT_MS`; past it the
 *   page renders and still moves when the answer arrives.
 * - Cached Production: renders at once and moves only if the fresh answer says Testing.
 */
export async function applyBrandDefaultEnvironment(brand: {
  brandSite: string;
  stagingWebHost?: string;
}): Promise<boolean> {
  if (typeof window === 'undefined' || !brand.stagingWebHost) return false;
  const target = getCurrentRuntimeTarget();
  if (target !== 'production') return false;
  // Another host of the same build (a dealership domain, a preview): nothing to ask.
  if (!isBrandProductionHost(window.location.hostname, brand.brandSite)) return false;

  const base = () => ({
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    brandSite: brand.brandSite,
    stagingWebHost: brand.stagingWebHost,
    target,
    visitorChoseProduction: readProductionChoice(),
  });

  const cached = readCachedEnvironmentSettings(brand.brandSite);
  const first = decideBrandEnvironmentRedirect({ ...base(), settings: cached });
  if (first.kind === 'storeChoice') {
    storeProductionChoice();
    window.history.replaceState(null, '', first.cleanedUrl);
    return false;
  }

  const ask = (timeoutMs?: number) =>
    fetchEnvironmentSettings({ brandSite: brand.brandSite, url: settingsGraphqlUrl(), timeoutMs });

  if (first.kind === 'redirect') {
    const fresh = await ask(BRAND_ENV_CONFIRM_TIMEOUT_MS);
    const second = fresh ? decideBrandEnvironmentRedirect({ ...base(), settings: fresh }) : first; // no answer in time: trust the cache
    if (second.kind === 'redirect') {
      window.location.replace(second.url);
      return true;
    }
    return false;
  }

  const moveLater = () => {
    void ask().then(fresh => {
      if (!fresh) return;
      const decision = decideBrandEnvironmentRedirect({ ...base(), settings: fresh });
      if (decision.kind === 'redirect') window.location.replace(decision.url);
    });
  };

  // Would a Testing answer move this page at all? Not on the admin, for a visitor who chose
  // Production, or on an in-flight return: then nothing is worth waiting for.
  const couldMove =
    decideBrandEnvironmentRedirect({ ...base(), settings: { defaultEnvironment: 'STAGING' } })
      .kind === 'redirect';

  if (!cached && couldMove) {
    const fresh = await ask(BRAND_ENV_FIRST_VISIT_TIMEOUT_MS);
    if (!fresh) {
      moveLater();
      return false;
    }
    const decision = decideBrandEnvironmentRedirect({ ...base(), settings: fresh });
    if (decision.kind === 'redirect') {
      window.location.replace(decision.url);
      return true;
    }
    return false;
  }

  moveLater();
  return false;
}
